import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { X, Link2, ArrowLeftRight, Search, Zap } from "lucide-react";

const COMPANY = {
  misas: { label: "Misaş", cls: "company-misas" },
  mis_holding: { label: "Mis Holding", cls: "company-mh" },
  koop: { label: "Mis Koop.", cls: "company-koop" },
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function LinkDevirDialog({ open, historyId, onClose, onLinked }) {
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState(null);
  const [q, setQ] = useState("");
  const [yk, setYk] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !historyId) return;
    setLoading(true); setPicked(null); setQ(""); setYk(""); setSource(null); setCandidates([]);
    axios.get(`${API}/history/link-candidates/${historyId}`, { withCredentials: true })
      .then(r => { setSource(r.data.source); setCandidates(r.data.candidates || []); })
      .catch(e => toast.error(e?.response?.data?.detail || "Aday listesi yüklenemedi"))
      .finally(() => setLoading(false));
  }, [open, historyId]);

  const query = q.trim().toLocaleLowerCase("tr-TR");
  const visible = useMemo(() => candidates.filter(c => {
    if (!query) return true;
    return (
      (c.member_ad_soyad || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (c.changed_by_name || "").toLocaleLowerCase("tr-TR").includes(query)
    );
  }), [candidates, query]);

  if (!open) return null;

  const submit = async () => {
    if (!picked) { toast.error("Bir aday seçin"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/history/link`, {
        history_id_a: historyId,
        history_id_b: picked.id,
        yk_karar_no: yk.trim(),
      }, { withCredentials: true });
      toast.success("Kayıtlar başarıyla eşleştirildi");
      onLinked?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eşleştirme başarısız");
    } finally { setBusy(false); }
  };

  const srcCo = source ? (COMPANY[source.company] || { label: source.company, cls: "" }) : null;
  const srcSign = source && (source.delta || 0) > 0 ? "artış" : "azalış";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="link-dialog">
      <div className="crisp-card p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2"><Link2 size={18}/> Manuel Devir Eşleştirme</h3>
            <p className="text-xs text-slate-500 mt-0.5">Eşleşmeyen bu hisse değişikliği için karşı tarafı seçin; sistem iki kaydı devir çifti olarak birleştirir.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={18}/></button>
        </div>

        {source && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${srcCo.cls}`}>{srcCo.label}</span>
              <div className="text-sm font-bold text-slate-900">{source.member_ad_soyad || "—"}</div>
              <div className={`text-xs font-bold font-mono-nums ${source.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                {source.delta > 0 ? "+" : ""}{source.delta} ({srcSign})
              </div>
              <div className="text-xs text-slate-500 font-mono-nums">{fmt(source.changed_at)}</div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="link-search" value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Karşı taraftaki ortağı veya işlemi yapan yöneticiyi ara..."
                   className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
          </div>
          <div className="text-[11px] text-slate-500 font-mono-nums whitespace-nowrap">{visible.length} aday</div>
        </div>

        <div className="border border-slate-200 rounded-md max-h-[42vh] overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Yükleniyor...</div>
          ) : visible.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">Uygun aday bulunamadı.</div>
          ) : visible.map(c => {
            const isPicked = picked?.id === c.id;
            return (
              <button key={c.id} type="button" onClick={() => setPicked(c)}
                data-testid={`link-cand-${c.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-0 flex items-center gap-3 flex-wrap transition-colors ${isPicked ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold truncate ${isPicked ? "text-white" : "text-slate-900"}`}>{c.member_ad_soyad || "—"}</div>
                  <div className={`text-[11px] font-mono-nums truncate ${isPicked ? "text-slate-300" : "text-slate-500"}`}>{fmt(c.changed_at)} · {c.changed_by_name || c.changed_by || "—"}</div>
                </div>
                <div className={`text-xs font-bold font-mono-nums ${isPicked ? "text-white" : (c.delta > 0 ? "text-emerald-600" : "text-red-600")}`}>
                  {c.delta > 0 ? "+" : ""}{c.delta}
                </div>
                {c.is_exact_match && (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${isPicked ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-800"}`}>
                    <Zap size={10}/> Tam Eşleşme
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Yönetim Kurulu Karar No <span className="text-slate-400 font-normal normal-case">(opsiyonel)</span></label>
          <input data-testid="link-yk" value={yk} onChange={e => setYk(e.target.value)}
                 placeholder="Ör. 2026/07"
                 className="w-full px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
        </div>

        <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100 gap-3 flex-wrap">
          <div className="text-[11px] text-slate-500 flex items-center gap-1"><ArrowLeftRight size={11}/> Eşleştirme yalnızca aynı şirkette, zıt yönlü değişiklikler için mümkündür.</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">İptal</button>
            <button onClick={submit} disabled={!picked || busy} data-testid="link-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
              <Link2 size={14}/> {busy ? "Eşleştiriliyor..." : "Eşleştir"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
